import type { TenantSession } from '@classgo/shared';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { initialSedeId, rememberSede } from './sede-preference';
import type { TenantContextValue } from './types';

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * Which school and which sede the screens are looking at. The sede choice is remembered per
 * tenant in `localStorage`; the main sede is the default.
 */
export const TenantProvider = ({
  session,
  children,
}: {
  session: TenantSession;
  children: ReactNode;
}) => {
  const [sedeId, setSedeId] = useState(() =>
    initialSedeId(session.sedes, session.tenant.id, window.localStorage),
  );
  const selectSede = useCallback(
    (id: string) => {
      if (!session.sedes.some((sede) => sede.id === id)) return;
      setSedeId(id);
      rememberSede(session.tenant.id, id, window.localStorage);
    },
    [session],
  );
  const value = useMemo<TenantContextValue | null>(() => {
    const sede = session.sedes.find((candidate) => candidate.id === sedeId) ?? session.sedes[0];
    return sede ? { session, sede, selectSede } : null;
  }, [session, sedeId, selectSede]);
  if (!value) {
    throw new Error(`Tenant ${session.tenant.id} has no sedes`);
  }
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

/**
 * The tenant context.
 *
 * @returns Session, selected sede and the selector.
 */
export const useTenant = (): TenantContextValue => {
  const value = useContext(TenantContext);
  if (!value) throw new Error('useTenant needs a TenantProvider');
  return value;
};
